import React, { lazy, Suspense } from 'react';

import * as Sentry from '@sentry/react';
import { useRoutes, Navigate } from 'react-router';

import { GetSignedUrlFile } from './components/GetSignedUrlFile';
import IsLoadingComponent from './components/PageLoading';
import Error500 from './containers/errors/500error';

// Must run after Sentry.init in instrument.js (imported first from index.js)
const useSentryRoutes = Sentry.wrapUseRoutesV7(useRoutes);

const withSuspense = (Component) => (props) => (
    <Suspense fallback={<IsLoadingComponent isLoading />}>
        <Component {...props} />
    </Suspense>
);

const UnauthorizedErrorPage = withSuspense(lazy(() => import('@src/containers/errors/UnauthorizedErrorPage')));

const NoStatesErrorPage = withSuspense(lazy(() => import('@src/containers/errors/NoStatesErrorPage')));

const AccessDeniedErrorPage = withSuspense(lazy(() => import('@src/containers/errors/AccessDeniedErrorPage')));

const LoggedOutPage = withSuspense(lazy(() => import('@src/containers/Auth/LoggedOutPage')));

const WelcomePage = withSuspense(lazy(() => import('@src/containers/Auth/WelcomePage')));

const LoginPage = withSuspense(lazy(() => import('@src/containers/login')));

/**
 * ## Error 404
 *
 * **Link:**
 * - `/erro404`
 *
 * **About:**  Display page can't be found
 */
const Error404 = withSuspense(lazy(() => import('./containers/errors/404error')));

/**
 * ## Error 403
 *
 * **Link:**
 * - `/authorization-error`
 *
 * **About:** Informs user they can't access this page due
 * to authorization error
 */
const Error403 = withSuspense(lazy(() => import('./containers/errors/403error')));

/**
 * ## Error 401
 *
 * **Link:**
 * - `/inactivity-error`
 *
 * **About:**  Users login credentials have timed out.
 * Prompt user to relogin
 */
const Error401 = withSuspense(lazy(() => import('./containers/errors/401error')));

/**
 * ## TrainingAndJobAidsArticles
 *
 * **Link:**
 * - `/training-and-job-aids/:id`
 * - `/training-and-job-aids/elearnings/:id`
 *
 * **About:** Training and Jobs Aids Articles: Training/ Webinars/ Job aids Articles
 *
 */
const TrainingAndJobAidsArticles = withSuspense(lazy(() => import('./containers/trainings-and-job-aids/id')));

const TrainingArticlePage = withSuspense(lazy(() => import('./containers/trainings-and-job-aids/elearnings/id')));

const JobAidPage = withSuspense(lazy(() => import('./containers/trainings-and-job-aids/job_aids/id')));

const WebinarPage = withSuspense(lazy(() => import('./containers/trainings-and-job-aids/webinars/id')));

/**
 * ## JobAidsSearchPages
 *
 * **Link:**
 * - `/training-and-job-aids/job_aids/:id`
 * - `/training-and-job-aids/job_aids/search:id`
 *
 * **About:** Training and Jobs Aids Articles: Training/ Webinars/ Job aids Articles
 *
 */
const JobAidsSearchPages = withSuspense(lazy(() => import('./containers/trainings-and-job-aids/job_aids')));

/**
 * ## TrainingAndJobAidsLiveWebinarSearchPages
 *
 * **Link:**
 * - `/training-and-job-aids/webinars/search:id`
 *
 * **About:** Training and Jobs Aids Webinars Search Page: Index View
 *
 */
const TrainingAndJobAidsLiveWebinarSearchPages = withSuspense(lazy(() => import('./containers/trainings-and-job-aids/webinars')));

/**
 * ## TrainingAndJobAidsSearchPages
 *
 * **Link:**
 * - `/training-and-job-aids/search`
 * - `/training-and-job-aids/search/:id`
 *
 * **About:** Training and Jobs Aids Articles Search Page: Index View
 *
 */
const TrainingAndJobAidsSearchPages = withSuspense(lazy(() => import('./containers/trainings-and-job-aids/elearnings')));

/**
 * ## TrainingAndJobAidsTake20Page
 *
 * **Link:**
 * - `/training-and-job-aids/take20`
 *
 * **About:** Training and Jobs Aids Take20 Page: Index View
 *
 */
const TrainingAndJobAidsTake20Page = withSuspense(lazy(() => import('./containers/trainings-and-job-aids/take20')));

/**
 * ## TrainingAndJobAids
 *
 * **Link:**
 * - `/training-and-job-aids`
 *
 * **About:** Training and Jobs Aids Home:
 * Leads to Training - or - Webinars search pages
 *
 */
const TrainingAndJobAids = withSuspense(lazy(() => import('./containers/trainings-and-job-aids')));

/**
 * ## Home
 *
 * **Link:**
 * - `/`
 *
 * **About:** Home page
 *
 */
const Home = withSuspense(lazy(() => import('./containers/home')));

/**
 * ## CredentialChecker
 *
 * **Link:**
 * - `N/A`
 *
 * **About:** This is the container that checks user has JWT Token
 *
 */
const CredentialChecker = withSuspense(lazy(() => import('./containers/App/CredentialChecker')));

/**
 * ## Guidesandmanuals
 *
 * **Link:**
 * - `/guides-and-rate-bulletins`
 *
 * **About:** Underwriting Guidelines and Rate Bulletins Index Page
 *
 */
const Guidesandmanuals = withSuspense(lazy(() => import('./containers/guides')));

/**
 * ## GuideOrManualView
 *
 * **Link:**
 * - `/guides-and-rate-bulletins/:id`
 *
 * **About:** Underwriting Guidelines and Rate Bulletins Article: View Page
 *
 */
const GuideOrManualView = withSuspense(lazy(() => import('./containers/guides/id')));

/**
 * ## ProductsSelectionPage
 *
 * **Link:**
 * - `/product`
 *
 * **About:** Products Home Page: Index Selection Page
 *
 */
const ProductsSelectionPage = withSuspense(lazy(() => import('./containers/products')));

/**
 * ## ProductsNewsAndUpdatesPage
 *
 * **Link:**
 * - `/products/product-news-and-updates/`
 * - `/products/product-news-and-updates?category-id[]=`
 *
 * **About:** Products Articles Search: Index Page
 *
 */
const ProductsNewsAndUpdatesPage = withSuspense(lazy(() => import('./containers/products/product-news-and-updates')));

/**
 * ## ProductsArticles
 *
 * **Link:**
 * - `/products/product-news-and-updates/:id`
 *
 * **About:** Products Articles: Article View Page
 *
 */
const ProductsArticles = withSuspense(lazy(() => import('./containers/products/product-news-and-updates/id')));

/**
 * ## ProductsAProductsAvailMaprticles
 *
 * **Link:**
 * - `/products/product-availability-map`
 * - `/products/product-availability-map/:id`
 *
 * **About:** Products Availability Map: View Page
 *
 */
const ProductsAvailMap = withSuspense(lazy(() => import('./containers/products/product-availability-map')));

/**
 * ## ProductsDisplaySingleProduct
 *
 * **Link:**
 * - `/products/classic/:id`
 * - `/products/:id`
 *
 * **About:** Products: View Page. Classic or New
 *
 */
const ProductsDisplaySingleProduct = withSuspense(lazy(() => import('./containers/products/id')));

/**
 * ## AlertsPage
 *
 * **Link:**
 * - `/alerts`
 *
 * **About:** Alerts: Index Page
 *
 */
const AlertsPage = withSuspense(lazy(() => import('./containers/alert')));

/**
 * ## AlertsArticlePage
 *
 * **Link:**
 * - `/alerts/:id`
 * - `/products/:id`
 *
 * **About:** Alerts Articles: View Page
 *
 */
const AlertsArticlePage = withSuspense(lazy(() => import('./containers/alert/id')));

const ImpactingPoliciesPage = withSuspense(lazy(() => import('./containers/impacting-policies')));

const ImpactingPolicyPage = withSuspense(lazy(() => import('./containers/impacting-policies/id')));

/**
 * ## ContactUsPage
 *
 * **Link:**
 * - `/contact-us`
 *
 * **About:** Contact Us: Displays all Contact Forms
 *
 */
const ContactUsPage = withSuspense(lazy(() => import('./containers/contact-us')));

const FavoritePagesPage = withSuspense(lazy(() => import('./containers/FavoritePages')));

// const RSMAgent360Page = Loadable({
//     loader: () => import('./containers/rsm/agent360'),
//     loading: isLoadingComponent,
// });

/**
 * ## RSMArticles
 *
 * **Link:**
 * - `/rsm/field-management-news/:id`
 *
 * **About:** Field Managment News - Article pages
 *
 */
// const RSMArticles = Loadable({
//    loader: () => import('./containers/rsm/field-management-news/id'),
//    loading: isLoadingComponent,
// });

/**
 * ## RSMFieldManagmentPage
 *
 * **Link:**
 * - `/rsm/field-management-news`
 *
 * **About:** RSM Field Management News Index view
 *
 */
// const RSMFieldManagmentPage = Loadable({
//     loader: () => import('./containers/rsm/field-management-news'),
//     loading: isLoadingComponent,
// });

/**
 * ## RSMPortalPage
 *
 * **Link:**
 * - `/rsm`
 *
 * **About:** RSM Home Page Containing all rsm linked pages
 *
 */
// const RSMPortalPage = Loadable({
//     loader: () => import('./containers/rsm'),
//     loading: isLoadingComponent,
// });

/**
 * ## Agent360ContainerPage
 *
 * **Link:**
 * - `/agent360`
 * - `/agent360/:id`
 *
 * **About:** Agent360 Container for agent360 sub pages
 *
 */
const Agent360ContainerPage = withSuspense(lazy(() => import('./containers/agent360')));

/**
 * ## Agent360ExpectPage
 *
 * **Link:**
 * - `/agent360`
 * - `/agent360/what-to-expect`
 *
 * **About:** Agent360 - What to expect sub page -default agent360 sub page
 *
 */
const Agent360ExpectPage = withSuspense(lazy(() => import('./containers/agent360/Agent360Expect')));

/**
 * ## Agent360ProductsPage
 *
 * **Link:**
 * - `/agent360/new-products`
 *
 * **About:** Agent360 - New Products sub page
 */
const Agent360ProductsPage = withSuspense(lazy(() => import('./containers/agent360/product/product')));

/**
 * ## Agent360TrainingPage
 *
 * **Link:**
 * - `/agent360/training`
 *
 * **About:** Agent360 - Trainings sub page
 */
const Agent360TrainingPage = withSuspense(lazy(() => import('./containers/agent360/Agent360TrainingPage')));

const SearchPage = withSuspense(lazy(() => import('./containers/search')));

const SitemapPage = withSuspense(lazy(() => import('./containers/sitemap')));

const DigitalAccessibilityPage = withSuspense(lazy(() => import('./containers/digital-accessibility')));

const CommunicationRequestForms = withSuspense(lazy(() => import('./containers/CommunicationRequestForms/CommunicationRequestForms')));

const OldSocialSharingsPage = withSuspense(lazy(() => import('./containers/OldSocialSharings')));

const CollateralsPage = withSuspense(lazy(() => import('./containers/collaterals/CollateralsPage')));

const CollateralTemplatesPage = withSuspense(lazy(() => import('./containers/collaterals/CollateralTemplatesPage')));

const CollateralTemplatePage = withSuspense(lazy(() => import('./containers/collaterals/CollateralTemplatePage')));

const SocialMediaPage = withSuspense(lazy(() => import('./containers/collaterals/SocialMediaPage')));

const HolidayGiftsPage = withSuspense(lazy(() => import('./containers/holiday-gifts/HolidayGiftsPage')));

const CollateralRequestsPage = withSuspense(lazy(() => import('./containers/my-account/CollateralRequests/CollateralRequestsPage')));

const CollateralRequestShowPage = withSuspense(lazy(() => import('./containers/my-account/CollateralRequests/CollateralRequestShowPage')));

const CheckoutPage = withSuspense(lazy(() => import('./containers/collateral-requests')));

// const CoBrandedVideosPage = Loadable({
//     loader: () => import('@src/containers/co-branded-videos'),
//     loading: isLoadingComponent,
// });

const SalesforceKnowledgebase = withSuspense(lazy(() => import('./containers/salesforce')));

/**
 * ## MyAccountPage
 *
 * **Link:**
 * - `/my-account`
 *
 * **About:** User account page with profile information and account management options
 */
const MyAccountPage = withSuspense(lazy(() => import('./containers/my-account')));

/**
 * ## AgencyLocationsPage
 *
 * **Link:**
 * - `/my-account/agency-locations`
 *
 * **About:** Agency locations management page for users with active Premier Access
 */
const AgencyLocationsPage = withSuspense(lazy(() => import('./containers/my-account/AgencyLocations/AgencyLocationsPage')));

/**
 * ## AgencyAccessPage
 *
 * **Link:**
 * - `/my-account/agency-access`
 *
 * **About:** Agency access management page for premier access group admins
 */
const AgencyAccessPage = withSuspense(lazy(() => import('./containers/my-account/AgencyAccess/AgencyAccessPage')));

/**
 * ## CustomerListsPage
 *
 * **Link:**
 * - `/my-account/customer-lists`
 *
 * **About:** Customer lists management page for users with active Premier Access
 */
const CustomerListsPage = withSuspense(lazy(() => import('./containers/my-account/CustomerLists/CustomerListsPage')));

/**
 * ## EditProfilePage
 *
 * **Link:**
 * - `/my-account/edit-profile`
 *
 * **About:** Edit profile page for users to update their contact information and agency logo
 */
const EditProfilePage = withSuspense(lazy(() => import('./containers/my-account/EditProfile')));

export function AppRoutes() {
    return useSentryRoutes([
        { path: '/unauthorized', element: <UnauthorizedErrorPage /> },
        { path: '/no-states', element: <NoStatesErrorPage /> },
        { path: '/access-denied', element: <AccessDeniedErrorPage /> },
        { path: '/logged-out', element: <LoggedOutPage /> },
        { path: '/welcome', element: <WelcomePage /> },
        { path: '/login', element: <LoginPage /> },
        {
            element: <CredentialChecker />,
            children: [
                { path: '/attachments/*', element: <GetSignedUrlFile /> },
                { path: '/favorites', element: <FavoritePagesPage /> },
                { path: '/alerts/:id', element: <AlertsArticlePage /> },
                { path: '/alerts', element: <AlertsPage /> },
                { path: '/impacted-policies/:id', element: <ImpactingPolicyPage /> },
                { path: '/impacted-policies', element: <ImpactingPoliciesPage /> },
                { path: '/communication-request-forms', element: <CommunicationRequestForms /> },
                // {
                //     component: RSMAgent360Page,
                //     path: '/field-management/agent360/:id',
                // },
                // {
                //     component: RSMAgent360Page,
                //     path: '/field-management/agent360',
                // },
                // {
                //     component: RSMFieldManagmentPage,
                //     path: '/field-management/field-management-news',
                //     exact: true,
                // },
                // {
                //     component: RSMArticles,
                //     path: '/field-management/field-management-news/:id',
                //     exact: true,
                // },
                // {
                //     component: RSMArticles,
                //     path: '/field-management/:id',
                //     exact: true,
                // },
                // {
                //     component: RSMPortalPage,
                //     path: '/field-management',
                //     exact: true,
                // },
                { path: '/training-and-job-aids/webinars/search:id', element: <TrainingAndJobAidsLiveWebinarSearchPages /> },
                { path: '/training-and-job-aids/webinars/search', element: <TrainingAndJobAidsLiveWebinarSearchPages /> },
                { path: '/training-and-job-aids/webinars/', element: <TrainingAndJobAidsLiveWebinarSearchPages /> },
                { path: '/training-and-job-aids/elearnings/search', element: <TrainingAndJobAidsSearchPages /> },
                { path: '/training-and-job-aids/elearnings', element: <TrainingAndJobAidsSearchPages /> },
                { path: '/training-and-job-aids/take20', element: <TrainingAndJobAidsTake20Page /> },
                { path: '/training-and-job-aids/job-aids', element: <JobAidsSearchPages /> },
                { path: '/training-and-job-aids/job-aids/search', element: <JobAidsSearchPages /> },
                { path: '/training-and-job-aids/job-aids/search:id', element: <JobAidsSearchPages /> },
                { path: '/training-and-job-aids/job-aids/:id', element: <JobAidPage /> },
                { path: '/training-and-job-aids/webinars/:id', element: <WebinarPage /> },
                { path: '/training-and-job-aids/elearnings/:id', element: <TrainingArticlePage /> },
                { path: '/training-and-job-aids/:id', element: <TrainingAndJobAidsArticles /> },
                { path: '/training-and-job-aids', element: <TrainingAndJobAids /> },
                { path: '/guides-and-rate-bulletins/:id', element: <GuideOrManualView /> },
                { path: '/guides-and-rate-bulletins', element: <Guidesandmanuals /> },
                { path: '/products/product-news-and-updates/search:id', element: <ProductsNewsAndUpdatesPage /> },
                { path: '/products/product-news-and-updates/search', element: <ProductsNewsAndUpdatesPage /> },
                { path: '/products/product-news-and-updates/:id', element: <ProductsArticles /> },
                { path: '/products/product-availability-map/:id', element: <ProductsAvailMap /> },
                { path: '/products/product-availability-map', element: <ProductsAvailMap /> },
                { path: '/products/product-news-and-updates:id', element: <ProductsNewsAndUpdatesPage /> },
                { path: '/products/product-news-and-updates/', element: <ProductsNewsAndUpdatesPage /> },
                { path: '/products/classic/:id', element: <ProductsDisplaySingleProduct /> },
                { path: '/products/agent360/:id', element: <ProductsDisplaySingleProduct /> },
                { path: '/products/:id', element: <ProductsDisplaySingleProduct /> },
                { path: '/products', element: <ProductsSelectionPage /> },
                {
                    path: '/agent360',
                    element: <Agent360ContainerPage />,
                    children: [
                        { index: true, element: <Navigate to="what-to-expect" replace /> },
                        { path: 'what-to-expect', element: <Agent360ExpectPage /> },
                        { path: 'new-products', element: <Agent360ProductsPage /> },
                        { path: 'product-overview', element: <Agent360ProductsPage /> },
                        { path: 'training', element: <Agent360TrainingPage /> },
                    ],
                },
                { path: '/search', element: <SearchPage /> },
                { path: '/ready-to-use-social-media-posts', element: <OldSocialSharingsPage /> },
                { path: '/marketing-collateral', element: <CollateralsPage /> },
                { path: '/agency-growth-store', element: <CollateralTemplatesPage /> },
                { path: '/agency-growth-store/orders', element: <CollateralRequestsPage /> },
                { path: '/agency-growth-store/orders/:id', element: <CheckoutPage /> },
                { path: '/agency-growth-store/social-media/:id', element: <SocialMediaPage /> },
                { path: '/agency-growth-store/gifts', element: <HolidayGiftsPage /> },
                { path: '/agency-growth-store/:category/:id', element: <CollateralTemplatePage /> },
                { path: '/agency-growth-store/:category', element: <CollateralTemplatesPage /> },
                { path: '/contact-us', element: <ContactUsPage /> },
                // {
                //    component: CoBrandedVideosPage,
                //    path: '/co-branded-videos',
                //    excat: true,
                // },
                { path: '/sitemap', element: <SitemapPage /> },
                { path: '/digital-accessibility', element: <DigitalAccessibilityPage /> },
                { path: '/ask-foremost', element: <SalesforceKnowledgebase /> },
                { path: '/my-account', element: <MyAccountPage /> },
                { path: '/my-account/edit-profile', element: <EditProfilePage /> },
                { path: '/my-account/agency-locations', element: <AgencyLocationsPage /> },
                { path: '/my-account/customer-lists', element: <CustomerListsPage /> },
                { path: '/my-account/agency-access', element: <AgencyAccessPage /> },
                { path: '/my-account/orders', element: <CollateralRequestsPage /> },
                { path: '/my-account/orders/:id', element: <CollateralRequestShowPage /> },
                { path: '/authorization-error', element: <Error403 /> },
                { path: '/server-down-error', element: <Error500 /> },
                { path: '/inactivity-error', element: <Error401 /> },
                { path: '/', element: <Home /> },
                { path: '*', element: <Error404 /> },
            ],
        },
    ]);
}
